import { DEFAULT_ERROR_MESSAGE } from "@/common/constant";
import { tableResponseSchema, tableSchema } from "@/common/store/tableStore";
import { formatDateKST } from "@/common/utils/date";
import { z } from "zod";

export const pricePolicyTypeSchema = z.enum(["SINGLE", "TOU"]);
export type PricePolicyType = z.infer<typeof pricePolicyTypeSchema>;

export const PRICE_POLICY_TYPE_LABELS: Record<PricePolicyType, string> = {
  SINGLE: "단일 요금",
  TOU: "계시별 요금",
};

export const PRICE_POLICY_TYPE_OPTIONS = [
  { value: pricePolicyTypeSchema.enum.SINGLE, label: "단일 요금" },
  { value: pricePolicyTypeSchema.enum.TOU, label: "계시별 요금" },
];

export const seasonSchema = z.enum(["SUMMER", "SPRING_FALL", "WINTER"]);
export type Season = z.infer<typeof seasonSchema>;

/** 서버 `season_label` 은 한전 원문 표기("여름철")라 화면 문구는 FE 가 따로 갖는다 */
export const SEASON_LABELS: Record<Season, string> = {
  SUMMER: "여름",
  SPRING_FALL: "봄·가을",
  WINTER: "겨울",
};

export const SEASON_ORDER: Season[] = ["SUMMER", "SPRING_FALL", "WINTER"];

export const loadPeriodSchema = z.enum(["OFF_PEAK", "MID_PEAK", "PEAK"]);
export type LoadPeriod = z.infer<typeof loadPeriodSchema>;

export const LOAD_PERIOD_LABELS: Record<LoadPeriod, string> = {
  OFF_PEAK: "경부하",
  MID_PEAK: "중간부하",
  PEAK: "최대부하",
};

export const LOAD_PERIOD_ORDER: LoadPeriod[] = [
  "OFF_PEAK",
  "MID_PEAK",
  "PEAK",
];

/** 서버가 `Decimal` 을 문자열("218.90")로 주므로 끝자리 0 만 떼고 값은 건드리지 않는다 */
export function formatUnitPrice(
  value: string | number | null | undefined,
): string {
  if (value == null || value === "") return "-";
  const text = String(value);
  if (!text.includes(".")) return text;
  return text.replace(/\.?0+$/, "");
}

/** 서버 범위 문자열("142.4~263.1") → 화면 표기("142.4 ~ 263.1원") */
export function formatPriceRangeLabel(
  range: string | null | undefined,
): string {
  if (!range) return "-원";
  return `${range.replace("~", " ~ ")}원`;
}

export function toPriceNumber(
  value: string | number | null | undefined,
): number | null {
  if (value == null || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num : null;
}

export const pricePolicySearchTypeSchema = z.enum([
  "name",
  "member_unit_price",
  "guest_unit_price",
]);
export type PricePolicySearchType = z.infer<typeof pricePolicySearchTypeSchema>;

export const PRICE_POLICY_SEARCH_TYPE_OPTIONS = [
  { value: pricePolicySearchTypeSchema.enum.name, label: "요금제명" },
  {
    value: pricePolicySearchTypeSchema.enum.member_unit_price,
    label: "회원 요금단가",
  },
  {
    value: pricePolicySearchTypeSchema.enum.guest_unit_price,
    label: "비회원 요금단가",
  },
];

export const HAS_PENDING_OPTIONS = [
  { value: "true", label: "예약 있음" },
  { value: "false", label: "예약 없음" },
];

/** 체크박스 필터지만 API 는 단일 값만 받는다 — 둘 다 고르면 파라미터를 보내지 않는다 */
const singleValueFilter = <T extends z.ZodTypeAny>(item: T) =>
  z
    .array(item)
    .nullish()
    .transform((values) =>
      values?.length === 1 ? (values[0] as z.infer<T>) : undefined,
    );

export const requestPricePolicySchema = z.object({
  search_type: pricePolicySearchTypeSchema.nullish(),
  keyword: z.string().max(100).nullish(),
  price_policy_type: singleValueFilter(pricePolicyTypeSchema),
  has_pending: singleValueFilter(z.boolean()),
  from_created_at: z.string().nullish(),
  to_created_at: z.string().nullish(),
  from_updated_at: z.string().nullish(),
  to_updated_at: z.string().nullish(),
});
export type RequestPricePolicy = z.infer<typeof requestPricePolicySchema>;

export const requestPricePolicyListSchema = tableSchema.extend(
  requestPricePolicySchema.shape,
);
export type RequestPricePolicyList = z.infer<
  typeof requestPricePolicyListSchema
>;

export const pricePolicyItemSchema = z
  .object({
    id: z.number(),
    uuid: z.string(),
    name: z.string(),
    price_policy_type: pricePolicyTypeSchema.nullish(),
    member_unit_price_range: z.string().nullish(),
    guest_unit_price_range: z.string().nullish(),
    charger_connector_count: z.number(),
    has_pending: z.boolean(),
    created_at: z.string().nullish(),
    updated_at: z.string().nullish(),
  })
  .transform(({ created_at, updated_at, ...rest }) => ({
    created_at:
      typeof created_at === "string" ? formatDateKST(created_at) : created_at,
    updated_at:
      typeof updated_at === "string" ? formatDateKST(updated_at) : updated_at,
    ...rest,
  }));
export type PricePolicyItem = z.infer<typeof pricePolicyItemSchema>;

export const responsePricePolicyListSchema =
  tableResponseSchema(pricePolicyItemSchema);
export type ResponsePricePolicyList = z.infer<
  typeof responsePricePolicyListSchema
>;

export const touPriceSchema = z.object({
  season: seasonSchema,
  load_period: loadPeriodSchema,
  member_unit_price: z.string(),
  guest_unit_price: z.string(),
});
export type TouPrice = z.infer<typeof touPriceSchema>;

export const pricePolicyRateSchema = z.object({
  id: z.number(),
  uuid: z.string(),
  price_policy_type: pricePolicyTypeSchema,
  effective_from: z.string(),
  member_unit_price: z.string().nullish(),
  guest_unit_price: z.string().nullish(),
  tou_prices: z.array(touPriceSchema).nullish(),
  created_at: z.string().nullish(),
});
export type PricePolicyRate = z.infer<typeof pricePolicyRateSchema>;

export const pricePolicyDetailSchema = z.object({
  id: z.number(),
  uuid: z.string(),
  name: z.string(),
  memo: z.string().nullish(),
  charger_connector_count: z.number(),
  current_rate: pricePolicyRateSchema.nullish(),
  pending_rate: pricePolicyRateSchema.nullish(),
  created_at: z.string().nullish(),
  updated_at: z.string().nullish(),
});
export type PricePolicyDetail = z.infer<typeof pricePolicyDetailSchema>;

/** 응답의 9칸 순서는 보장되지 않아 (계절, 부하시간대) 로 찾는다 */
export function findTouPrice(
  prices: TouPrice[] | null | undefined,
  season: Season,
  loadPeriod: LoadPeriod,
): TouPrice | undefined {
  return prices?.find(
    (price) => price.season === season && price.load_period === loadPeriod,
  );
}

export const appliedPriceSchema = z.object({
  as_of: z.string(),
  rate_uuid: z.string().nullish(),
  price_policy_type: pricePolicyTypeSchema,
  season: seasonSchema.nullish(),
  load_period: loadPeriodSchema.nullish(),
  member_unit_price: z.string().nullish(),
  guest_unit_price: z.string().nullish(),
});
export type AppliedPrice = z.infer<typeof appliedPriceSchema>;

export const loadPeriodHoursSchema = z.object({
  as_of: z.string().nullish(),
  effective_from: z.string().nullish(),
  season_months: z
    .array(z.object({ season: seasonSchema, month: z.number() }))
    .nullish(),
  items: z
    .array(
      z.object({
        season: seasonSchema,
        load_period: loadPeriodSchema,
        from_hour: z.number(),
        to_hour: z.number(),
        hours_label: z.string().nullish(),
      }),
    )
    .nullish(),
});
export type LoadPeriodHours = z.infer<typeof loadPeriodHoursSchema>;

/** 서버가 계절별 월을 낱개로 주므로 연속 구간을 `~`, 끊긴 구간을 `,` 로 잇는다 */
export function formatSeasonMonths(
  seasonMonths: LoadPeriodHours["season_months"],
  season: Season,
): string {
  const months = (seasonMonths ?? [])
    .filter((entry) => entry.season === season)
    .map((entry) => entry.month)
    .sort((a, b) => a - b);
  if (months.length === 0) return "";

  const ranges: [number, number][] = [];
  for (const month of months) {
    const last = ranges.at(-1);
    if (last && month === last[1] + 1) last[1] = month;
    else ranges.push([month, month]);
  }

  // 겨울(11·12·1·2월)처럼 해를 넘는 계절은 12월과 1월을 잇는다 — "11~2월"
  if (ranges.length > 1 && ranges[0][0] === 1 && ranges.at(-1)![1] === 12) {
    const head = ranges.shift()!;
    ranges[ranges.length - 1][1] = head[1];
  }

  return ranges
    .map(([from, to]) => (from === to ? `${from}월` : `${from}~${to}월`))
    .join(", ");
}

/** "(2026. 6. 1 기준)" — 한전 구분 시행일 표기 */
export function formatEffectiveFromLabel(
  effectiveFrom: string | null | undefined,
): string {
  if (!effectiveFrom) return "";
  const [year, month, day] = effectiveFrom.slice(0, 10).split("-");
  if (!year || !month || !day) return "";
  return `(${year}. ${Number(month)}. ${Number(day)} 기준)`;
}

/** 한 칸에 여러 시간 구간이 올 수 있다 */
export function findLoadPeriodHours(
  items: LoadPeriodHours["items"],
  season: Season,
  loadPeriod: LoadPeriod,
): string[] {
  return (items ?? [])
    .filter(
      (item) => item.season === season && item.load_period === loadPeriod,
    )
    .map(
      (item) =>
        item.hours_label ??
        `${String(item.from_hour).padStart(2, "0")}:00~${String(item.to_hour % 24).padStart(2, "0")}:00`,
    );
}

const touPriceRequestSchema = z.object({
  season: seasonSchema,
  load_period: loadPeriodSchema,
  member_unit_price: z.string(),
  guest_unit_price: z.string(),
});

export const pricePolicyCreateRequestSchema = z.object({
  price_policy_type: pricePolicyTypeSchema,
  name: z.string().max(40).nullish(),
  memo: z.string().nullish(),
  member_unit_price: z.string().nullish(),
  guest_unit_price: z.string().nullish(),
  tou_prices: z.array(touPriceRequestSchema).nullish(),
});
export type PricePolicyCreateRequest = z.infer<
  typeof pricePolicyCreateRequestSchema
>;

export const pricePolicyReservationRequestSchema = z.object({
  price_policy_type: pricePolicyTypeSchema,
  effective_from: z.string(),
  member_unit_price: z.string().nullish(),
  guest_unit_price: z.string().nullish(),
  tou_prices: z.array(touPriceRequestSchema).nullish(),
});
export type PricePolicyReservationRequest = z.infer<
  typeof pricePolicyReservationRequestSchema
>;

/** 수정은 요금제명·메모만 받는다 — 단가는 변경 예약으로만 바뀐다 */
export const pricePolicyUpdateRequestSchema = z.object({
  name: z.string().nonempty(DEFAULT_ERROR_MESSAGE).max(40, "최대 40자예요."),
  memo: z.string().nullish(),
});
export type PricePolicyUpdateRequest = z.infer<
  typeof pricePolicyUpdateRequestSchema
>;

export const namePreviewSchema = z.object({ name: z.string() });

/** 충전기 화면의 `price_policy` 임베드와 요금제 드롭다운이 함께 쓰는 모양 */
export const pricePolicyEmbedSchema = z.object({
  id: z.number(),
  uuid: z.string(),
  name: z.string(),
  /** 시행된 단가표가 없으면(변경 예약만 있는 상태) null 이고 범위는 빈 문자열이다 */
  price_policy_type: pricePolicyTypeSchema.nullish(),
  member_unit_price_range: z.string().nullish(),
  guest_unit_price_range: z.string().nullish(),
});
export type PricePolicyEmbed = z.infer<typeof pricePolicyEmbedSchema>;

export const pricePolicyNameListSchema = pricePolicyEmbedSchema.array();
export type PricePolicyNameList = z.infer<typeof pricePolicyNameListSchema>;

export type FilterState = {
  search_type: string | null;
  keyword: string | null;
  price_policy_type: PricePolicyType[] | null;
  has_pending: boolean[] | null;
  from_created_at: string | null;
  to_created_at: string | null;
  from_updated_at: string | null;
  to_updated_at: string | null;
};
